// The company side of break-glass (M5-05; 05 §Agency cross-company access):
// - `BreakGlassBanner`, in the company shell for its managers (owners, admins, agency operators):
//   an agency session that is live now (who, why, until when; "End now" for owners and admins) or
//   waiting for a decision (the owner approves or rejects). It reads the newest page of
//   `GET /v1/companies/{co}/break-glass`, one cached call per company shared with the Setup page
//   below (the shell's read, like `/v1/me`: it refetches on focus and every minute).
// - `CompanyBreakGlassPage`, Setup › Break-glass: every session on the company, newest first,
//   with the same decisions; for owners and admins, the Break-glass log of every resource read.
// - BREAK-GLASS: the operator's own live session shows as a red banner (time left, "End now"),
//   and a private channel it opened only through the session carries "Viewed under break-glass".
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlertIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { isManager } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { DENSE_TABLE } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import {
  DecisionText,
  durationText,
  RejectDialog,
  SessionStateBadge,
  SessionTimes,
  scopeText,
} from "./agency-parts";
import {
  agencyKeys,
  type BreakGlassRead,
  type BreakGlassSession,
  companyBreakGlassQuery,
  companyBreakGlassReadsQuery,
} from "./agency-queries";

const isOwner = (c: CompanyAccess) => c.roles.includes("owner");
const endsHere = (c: CompanyAccess) => c.roles.some((r) => r === "owner" || r === "admin");

/** Approve, reject and end on the company side, with the company's rules on who may. */
function useCompanyDecisions(api: Api, company: CompanyAccess, s: BreakGlassSession) {
  const qc = useQueryClient();
  const refresh = () =>
    qc.invalidateQueries({ queryKey: agencyKeys.companyBreakGlassAll(company.id) });
  const params = { co: company.id, id: s.id };
  const approve = useMutation({
    mutationFn: () =>
      api.client.breakGlass.approve({ params, idempotencyKey: newIdempotencyKey() }),
    onSuccess: async () => {
      toast.add({ title: `Access approved for ${s.requested_by_name}`, type: "success" });
      await refresh();
    },
  });
  const reject = useMutation({
    mutationFn: (reason: string) =>
      api.client.breakGlass.reject({
        params,
        idempotencyKey: newIdempotencyKey(),
        body: { reason },
      }),
    onSuccess: refresh,
  });
  const end = useMutation({
    mutationFn: () =>
      api.client.breakGlass.end({ params, idempotencyKey: newIdempotencyKey(), body: {} }),
    onSuccess: async () => {
      toast.add({ title: "Break-glass session ended", type: "success" });
      await refresh();
    },
  });
  return { approve, reject, end };
}

function Decisions({
  api,
  company,
  s,
  size = "sm",
}: {
  api: Api;
  company: CompanyAccess;
  s: BreakGlassSession;
  size?: "xs" | "sm" | "default";
}) {
  const { approve, reject, end } = useCompanyDecisions(api, company, s);
  const [rejecting, setRejecting] = useState(false);
  const decides = s.state === "requested" && isOwner(company);
  const ends = s.state === "active" && endsHere(company);
  if (!decides && !ends) return null;
  return (
    <div
      className={size === "xs" ? "flex items-center gap-1" : "flex flex-wrap items-center gap-2"}
    >
      {decides ? (
        <>
          <Button size={size} disabled={approve.isPending} onClick={() => approve.mutate()}>
            Approve
          </Button>
          <Button size={size} variant="secondary" onClick={() => setRejecting(true)}>
            Reject
          </Button>
          <RejectDialog
            open={rejecting}
            onOpenChange={setRejecting}
            who={s.requested_by_name}
            pending={reject.isPending}
            onReject={(reason) => reject.mutate(reason, { onSuccess: () => setRejecting(false) })}
          />
        </>
      ) : null}
      {ends ? (
        <Button
          size={size}
          variant="destructive"
          disabled={end.isPending}
          onClick={() => end.mutate()}
        >
          {end.isPending ? "Ending…" : "End now"}
        </Button>
      ) : null}
    </div>
  );
}

// BREAK-GLASS: the operator's own side of a live session -----------------------------------------

/** The viewer's own session on the company that is live at `now` (an agency operator's). */
export function ownLiveSession(
  rows: readonly BreakGlassSession[],
  userId: string,
  now: Date,
): BreakGlassSession | null {
  return (
    rows.find(
      (s) =>
        s.state === "active" &&
        s.requested_by_user_id === userId &&
        s.expires_at !== null &&
        new Date(s.expires_at).getTime() > now.getTime(),
    ) ?? null
  );
}

/**
 * Whether a channel an agency operator sees is open to it only by its live session (the API's
 * rule, auth/break-glass.ts): a private, non-DM channel inside the session's scope, seen without
 * a membership of the company (an operator is never a member of a private channel).
 */
export function viewedUnderBreakGlass(
  s: BreakGlassSession | null,
  ch: { id: string; kind: string; is_private: boolean; space_id: string | null },
  hasMembership: boolean,
): boolean {
  if (!s || hasMembership || !ch.is_private) return false;
  if (ch.kind === "dm" || ch.kind === "group_dm") return false;
  if (s.scope === "company") return true;
  if (s.scope === "space") return ch.space_id === s.scope_id;
  return ch.id === s.scope_id;
}

/** "1 h 05 min left", "4 min left", "under a minute left". */
export function timeLeftText(expiresAt: string, now: Date): string {
  const m = Math.floor((new Date(expiresAt).getTime() - now.getTime()) / 60_000);
  if (m < 1) return "under a minute left";
  if (m < 60) return `${m} min left`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min left`;
}

function useNow(everyMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

/** The viewer's live session here, re-read with the shell's banner query. */
export function useOwnBreakGlass(
  api: Api,
  company: CompanyAccess,
  userId: string,
): BreakGlassSession | null {
  const operator = company.roles.includes("agency_operator");
  const q = useQuery({ ...companyBreakGlassQuery(api, company.id), enabled: operator });
  const now = useNow(15_000);
  return operator ? ownLiveSession(q.data?.data ?? [], userId, now) : null;
}

/** The "Viewed under break-glass" mark on content only the session opened. */
export function ViewedUnderBreakGlassBadge() {
  return (
    <Badge variant="danger" data-slot="viewed-under-break-glass">
      <ShieldAlertIcon aria-hidden className="size-3" />
      Viewed under break-glass
    </Badge>
  );
}

/**
 * The operator's persistent red banner while its own session is live: the scope, the time left
 * (ticking), and "End now" (the operator console's end route). When the clock runs out the
 * session reads expired and the company's reads are refetched, so nothing it opened stays shown.
 */
function OwnSessionBanner({
  api,
  company,
  s,
  now,
}: {
  api: Api;
  company: CompanyAccess;
  s: BreakGlassSession;
  now: Date;
}) {
  const qc = useQueryClient();
  const end = useMutation({
    mutationFn: () =>
      api.client.operator.breakGlass.end({
        params: { session_id: s.id },
        idempotencyKey: newIdempotencyKey(),
        body: {},
      }),
    onSuccess: async () => {
      toast.add({ title: "Break-glass session ended", type: "success" });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["company", company.id] }),
        qc.invalidateQueries({ queryKey: agencyKeys.operatorBreakGlass() }),
      ]);
    },
  });
  return (
    <Notice
      tone="danger"
      icon={ShieldAlertIcon}
      title={`You are in ${company.name} under break-glass`}
      action={
        <Button
          size="sm"
          variant="destructive"
          disabled={end.isPending}
          onClick={() => end.mutate()}
        >
          {end.isPending ? "Ending…" : "End now"}
        </Button>
      }
      data-session={s.id}
      data-slot="own-break-glass"
    >
      {scopeText(s)} ·{" "}
      <span data-slot="break-glass-time-left">{timeLeftText(s.expires_at ?? "", now)}</span>. Every
      private channel you open with it is logged and shown to the company's owners.
    </Notice>
  );
}

export function BreakGlassBanner({
  api,
  company,
  userId = null,
}: {
  api: Api;
  company: CompanyAccess;
  /** The viewer's user id: its own live session shows as the red operator banner. */
  userId?: string | null;
}) {
  const manager = isManager(company);
  const q = useQuery({ ...companyBreakGlassQuery(api, company.id), enabled: manager });
  const qc = useQueryClient();
  const now = useNow(15_000);
  const rows = q.data?.data ?? [];
  const own = userId ? ownLiveSession(rows, userId, now) : null;
  const ownId = own?.id ?? null;
  // The moment the viewer's session runs out, what it opened is refetched (and is then 404).
  const [wasLive, setWasLive] = useState<string | null>(null);
  useEffect(() => {
    if (ownId) setWasLive(ownId);
    else if (wasLive) {
      setWasLive(null);
      void qc.invalidateQueries({ queryKey: ["company", company.id] });
    }
  }, [ownId, wasLive, qc, company.id]);
  if (!manager) return null;
  const live = rows.filter(
    (s) => (s.state === "active" || s.state === "requested") && s.id !== ownId,
  );
  if (!live.length && !own) return null;
  return (
    <div className="mb-4 flex flex-col gap-2" data-slot="break-glass-banner">
      {own ? <OwnSessionBanner api={api} company={company} s={own} now={now} /> : null}
      {live.map((s) =>
        s.state === "active" ? (
          <Notice
            key={s.id}
            tone="warning"
            icon={ShieldAlertIcon}
            title={`Break-glass access is live: ${s.requested_by_name} (agency)`}
            action={<Decisions api={api} company={company} s={s} />}
            data-session={s.id}
          >
            {scopeText(s)} until {formatDateTime(s.expires_at ?? "")}. Reason: “{s.reason}”
          </Notice>
        ) : (
          <Notice
            key={s.id}
            tone="info"
            icon={ShieldAlertIcon}
            title={`${s.requested_by_name} (agency) asks for break-glass access`}
            action={<Decisions api={api} company={company} s={s} />}
            data-session={s.id}
          >
            {scopeText(s)} for {durationText(s.duration_minutes)}. Reason: “{s.reason}”
            {isOwner(company) ? null : " Waiting for the owner's decision."}
          </Notice>
        ),
      )}
    </div>
  );
}

export function CompanyBreakGlassPage({ api, company }: { api: Api; company: CompanyAccess }) {
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors.at(-1) ?? null;
  const q = useQuery(companyBreakGlassQuery(api, company.id, cursor));
  const rows = q.data?.data ?? [];
  return (
    <SetupPage title="Break-glass" {...(q.data ? { count: rows.length } : {})}>
      {q.isPending ? (
        <Loading label="Loading break-glass sessions" rows={4} />
      ) : q.isError ? (
        <LoadError what="Break-glass sessions" error={q.error} />
      ) : rows.length === 0 ? (
        <EmptyState variant="card" icon={ShieldAlertIcon} title="No break-glass sessions" />
      ) : (
        <>
          <Table data-slot="company-break-glass" className={DENSE_TABLE}>
            <TableCaption className="sr-only">
              Break-glass sessions, newest first.{" "}
              {isOwner(company)
                ? "You decide requests as the owner."
                : "The owner decides requests."}
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Session</TableHead>
                <TableHead scope="col">Requested by</TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  Reason
                </TableHead>
                <TableHead scope="col" className="max-lg:hidden">
                  When
                </TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  Decision
                </TableHead>
                <TableHead scope="col" className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} data-session={s.id} data-session-state={s.state}>
                  <TableHead scope="row" className="font-normal">
                    <div className="flex flex-col items-start gap-1">
                      <SessionStateBadge state={s.state} />
                      <span className="text-xs text-muted-foreground">
                        {scopeText(s)} · {durationText(s.duration_minutes)}
                      </span>
                    </div>
                  </TableHead>
                  <TableCell>{s.requested_by_name}</TableCell>
                  <TableCell className="max-w-72 whitespace-normal max-md:hidden">
                    {s.reason}
                  </TableCell>
                  <TableCell className="max-lg:hidden">
                    <SessionTimes s={s} />
                  </TableCell>
                  <TableCell className="whitespace-normal max-md:hidden">
                    <DecisionText s={s} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <Decisions api={api} company={company} s={s} size="xs" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {cursors.length > 1 || q.data.next_cursor ? (
            <div className="flex gap-2">
              {cursors.length > 1 ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setCursors((c) => c.slice(0, -1))}
                >
                  Newer
                </Button>
              ) : null}
              {q.data.next_cursor ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setCursors((c) => [...c, q.data.next_cursor])}
                >
                  Older
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
      {endsHere(company) ? <BreakGlassLog api={api} company={company} /> : null}
    </SetupPage>
  );
}

const READ_KIND: Record<BreakGlassRead["subject_kind"], string> = {
  channel: "Channel",
  topic: "Topic",
  thread: "Thread",
};

/**
 * BREAK-GLASS: Setup › Break-glass › Break-glass log, for owners and admins: every private
 * resource an agency user read under a session here (one row per resource per minute), newest
 * first, with the route that read it.
 */
function BreakGlassLog({ api, company }: { api: Api; company: CompanyAccess }) {
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors.at(-1) ?? null;
  const q = useQuery(companyBreakGlassReadsQuery(api, company.id, cursor));
  const rows = q.data?.data ?? [];
  return (
    <section className="mt-8 flex flex-col gap-3" aria-labelledby="break-glass-log">
      <h2 id="break-glass-log" className="font-heading text-lg font-strong text-foreground">
        Break-glass log
      </h2>
      {q.isPending ? (
        <Loading label="Loading the break-glass log" rows={3} />
      ) : q.isError ? (
        <LoadError what="The break-glass log" error={q.error} />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-slot="break-glass-log-empty">
          Nothing has been read under break-glass here.
        </p>
      ) : (
        <>
          <Table data-slot="break-glass-log">
            <TableCaption>
              Every private channel, topic or thread an agency user opened only through a session.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">When</TableHead>
                <TableHead scope="col">Who</TableHead>
                <TableHead scope="col">What</TableHead>
                <TableHead scope="col">Route</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-read={r.id} data-session={r.session_id}>
                  <TableCell>{formatDateTime(r.read_at)}</TableCell>
                  <TableCell>{r.agency_user_name}</TableCell>
                  <TableCell>
                    {READ_KIND[r.subject_kind]}{" "}
                    <span className="text-xs text-muted-foreground">…{r.subject_id.slice(-6)}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.route}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {cursors.length > 1 || q.data.next_cursor ? (
            <div className="flex gap-2">
              {cursors.length > 1 ? (
                <Button variant="secondary" onClick={() => setCursors((c) => c.slice(0, -1))}>
                  Newer
                </Button>
              ) : null}
              {q.data.next_cursor ? (
                <Button
                  variant="secondary"
                  onClick={() => setCursors((c) => [...c, q.data.next_cursor])}
                >
                  Older
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
